import {
    AssertionDescriptorDisplayCategoryEnum,
    AssessmentSectionId,
    EntityTypes,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    UserRightsEnum,
} from '../../utilities/Constants';
import { IDepositionModel, IDocumentModel } from '../../interfaces/ICase';

import { Assessment } from './assessment/Assessment';
import Authorization from '../../stores/Authorization';
import { CaseDeadlines } from './CaseDeadlines';
import { CaseDepositionListWrapper } from './static-sections/CaseDepositionListWrapper';
import { CaseExpertsList } from './static-sections/case-experts/CaseExpertsList';
import { CaseResolutionCaseFileSection } from '../case-resolution/CaseResolutionCaseFileSection';
import { DocumentTypeEnum } from '../../utilities/DbConstants';
import { DocumentsList } from '../documents-list/DocumentsList';
import { IAssociatedOrganizationModel } from '../../interfaces/IOrganization';
import { ICaseExpertModel } from '../../interfaces/ICaseExpert';
import { IDisplaySectionModel } from '../../interfaces/IDisplaySectionModel';
import { INoteModel } from '../../interfaces/INote';
import { IUserModel } from '../../interfaces/IUser';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { NoteListWrapper } from './static-sections/NoteListWrapper';
import Sort from '../../stores/Sort';
import { cloneDeep } from 'lodash';
import { useCallback } from 'react';
import { useCaseFileContext } from '../../contexts/useCaseFileContext';
import { useZoneContext } from '../../contexts/useZoneContext';

interface IStaticSectionRendererProps {
    isOpen: boolean;
    isReadOnlyMode: boolean;
    section: IDisplaySectionModel;
    user: IUserModel;
    zoneDefenseCounsels: IAssociatedOrganizationModel[];
    zoneUsers: IUserModel[];
}

export function StaticSectionRenderer({
    isOpen,
    isReadOnlyMode,
    section,
    user,
    zoneDefenseCounsels,
    zoneUsers,
}: IStaticSectionRendererProps) {
    const zoneContext = useZoneContext();

    const {
        currentCase,
        setCurrentCase,
        setCurrentCaseExpert,
        setCurrentNote,
        setOriginalCase,
        setForceNoteEditorInReadOnlyMode,
    } = useCaseFileContext();

    const handleAddNote = () => {
        const currentNote: INoteModel = {
            type: { id: NoteTypes.CaseNote },
            status: { id: NoteStatusTypes.Open },
            caseGuid: currentCase.guid!,
            purpose: { id: NotePurposeTypesEnum.Info },
        };

        setCurrentNote(currentNote);
        setForceNoteEditorInReadOnlyMode(false);
    };

    const handleViewEditNote = (noteGuidToEdit: string, readOnly?: boolean) => {
        if (!currentCase.notes) return;

        const currentNote = currentCase.notes.find((x) => x.guid == noteGuidToEdit);

        if (currentNote) {
            setCurrentNote(currentNote);
            setForceNoteEditorInReadOnlyMode(readOnly ?? false);
        }
    };

    const handleNoteListChanges = (notes: INoteModel[]) => {
        const caseCopy = cloneDeep(currentCase);
        caseCopy.notes = notes;

        setOriginalCase(caseCopy);
        setCurrentCase(caseCopy);
    };

    const handleDepositionsChange = (depositions: IDepositionModel[]) => {
        setOriginalCase((prev) => ({ ...prev, depositions }));
        setCurrentCase((prev) => ({ ...prev, depositions }));
    };

    const handleCaseExpertsChange = useCallback(
        (caseExperts: ICaseExpertModel[]) => {
            const caseCopy = cloneDeep(currentCase);
            caseCopy.caseExperts = caseExperts;

            setOriginalCase(caseCopy);
            setCurrentCase(caseCopy);
        },
        [currentCase, setCurrentCase, setOriginalCase]
    );

    const handleDocumentSaveComplete = (documents: IDocumentModel[]) => {
        const caseCopy = cloneDeep(currentCase);
        const docsCopy = [...(caseCopy.documents ?? [])];

        for (const newDoc of documents) {
            const foundIdx = docsCopy.findIndex((doc) => doc.guid === newDoc.guid) ?? -1;
            if (foundIdx > -1) {
                docsCopy[foundIdx] = newDoc;
            } else {
                docsCopy.push(newDoc);
            }
        }
        docsCopy.sort(Sort.compareDate('modifiedDate', undefined, 'desc'));
        caseCopy.documents = docsCopy;

        setOriginalCase(caseCopy);
        setCurrentCase(caseCopy);
    };

    const handleDocumentDeleteComplete = (documentGuid: string) => {
        const caseCopy = cloneDeep(currentCase);
        caseCopy.documents = caseCopy.documents?.filter((doc) => doc.guid !== documentGuid);
        setOriginalCase(caseCopy);
        setCurrentCase(caseCopy);
    };

    const handleCaseResolutionChange = useCallback(() => {
        // TODO: need to refresh case resolution data in case file
    }, [currentCase, setCurrentCase, setOriginalCase]);

    switch (section.id) {
        case AssertionDescriptorDisplayCategoryEnum.CaseNotes.Value:
            return (
                <NoteListWrapper
                    entityType={EntityTypes.CaseResolutionPlan}
                    user={user}
                    notes={currentCase.notes ?? []}
                    onAddNote={handleAddNote}
                    onViewEditNote={handleViewEditNote}
                    onRefreshParent={handleNoteListChanges}
                    readonly={isReadOnlyMode}
                    noteType={NoteTypes.CaseNote}
                />
            );

        case AssertionDescriptorDisplayCategoryEnum.OfficialCaseDocuments.Value:
            return (
                <></>
            );

        case AssertionDescriptorDisplayCategoryEnum.WorkProduct.Value:
            return (
                <DocumentsList
                    user={user}
                    entityGuid={currentCase.guid!}
                    entityTypeId={EntityTypes.Case}
                    documents={currentCase.documents}
                    parentEntityJson={JSON.stringify(currentCase)}
                    parentEntityRedirectUrl={LocalRoutes.CaseFile.replace(
                        ':guid',
                        currentCase.guid!
                    )}
                    authorizedToEdit={Authorization.userHasRight(
                        UserRightsEnum.EditCaseFile,
                        user,
                        zoneContext.zoneGuid
                    )}
                    documentTypeId={DocumentTypeEnum.Case}
                />
            );

        case AssertionDescriptorDisplayCategoryEnum.Deadlines.Value:
            return (
                <CaseDeadlines
                    user={user}
                    caseDeadlines={currentCase.caseDeadlines || []}
                    caseGuid={currentCase.guid!}
                    zoneContext={zoneContext}
                />
            );

        case AssertionDescriptorDisplayCategoryEnum.CheckmateAssessment.Value: {
            return (
                <div style={{ paddingBottom: '1em' }}>
                    <Assessment
                        expandedSections={[
                            AssessmentSectionId.BestFitCases,
                            AssessmentSectionId.CaseStatistics,
                        ]}
                        isOpen={isOpen}
                    />
                </div>
            );
        }
        case AssertionDescriptorDisplayCategoryEnum.Deposition.Value: {
            return (
                <>
                    {Authorization.userHasRight(
                        UserRightsEnum.ViewDeposition,
                        user,
                        zoneContext.zoneGuid
                    ) && (
                        <CaseDepositionListWrapper
                            user={user}
                            caseGuid={currentCase.guid}
                            currentCase={currentCase}
                            usersInZone={zoneUsers}
                            allDefenseCounselsForZone={zoneDefenseCounsels}
                            depositions={currentCase.depositions ?? []}
                            onDepositionsChange={handleDepositionsChange}
                            readonly={isReadOnlyMode}
                        />
                    )}
                </>
            );
        }

        case AssertionDescriptorDisplayCategoryEnum.CaseExperts.Value:
            return (
                <CaseExpertsList
                    user={user}
                    caseExperts={currentCase.caseExperts ?? []}
                    caseGuid={currentCase.guid}
                    refreshParent={handleCaseExpertsChange}
                    onSetCurrentCaseExpert={(caseExpert) => setCurrentCaseExpert(caseExpert)}
                    readonly={isReadOnlyMode}
                />
            );

        case AssertionDescriptorDisplayCategoryEnum.Documents.Value:
            const canEditCaseFile = Authorization.userHasRight(
                UserRightsEnum.EditCaseFile,
                user,
                zoneContext.zoneGuid
            );

            return (
                <>
                    {canEditCaseFile && currentCase.guid && (
                        <DocumentsList
                            authorizedToEdit={canEditCaseFile}
                            documents={currentCase.documents}
                            documentTypeId={DocumentTypeEnum.Case}
                            entityGuid={currentCase.guid}
                            entityTypeId={EntityTypes.Case}
                            uploadOnly={true}
                            user={user}
                            onSaveComplete={handleDocumentSaveComplete}
                            onDeleteComplete={handleDocumentDeleteComplete}
                        />
                    )}
                </>
            );

        case AssertionDescriptorDisplayCategoryEnum.CaseResolution.Value:
            return (
                <CaseResolutionCaseFileSection
                    caseGuid={currentCase.guid}
                    currentCase={currentCase}
                    readonly={isReadOnlyMode}
                    refreshParent={handleCaseResolutionChange}
                />
            );

        default:
            return null;
    }
}
